import type {
  Elemental,
  ElementalFile,
  ElementalFormData,
  ElementalType,
  ElementalTypeEnum,
  OwnershipEmailLeads,
  OwnershipInformation,
} from '../types/elemental'
import type { PROMPT_TYPES } from '../utils/constants'
import type { APIIntervals, APIOrderings, ItemType } from '../types/api'
import type { PromptUseCreate } from '../types/prompt'
import type { FavoritedItem } from '../types/user'
import { MOCKED_TYPES } from './@data/elemental-types'
import type { GetUserPromptMode } from './users'
import type { TagData } from './tags'
import {
  APIFetch,
  APINativeFetch,
  filterResponse,
  filterResponseBoolean,
} from '.'

const IS_API_AVAILABLE = true

// Elemental Types
export const getElementalTypes = async (): Promise<null | ElementalType[]> => {
  const url = '/elemental-types'
  if (!IS_API_AVAILABLE)
    return MOCKED_TYPES

  return filterResponse(
    await APIFetch(url, {
      method: 'GET',
    }),
  )
}

export interface UseElementalData {
  input: string
  styleId?: number
  languageId?: number
  toneId?: number
}

export interface ElementalQuery {
  page?: number
  limit?: number
  topic_id?: number | string
  size?: number
  order_dir?: 'asc' | 'desc'
  order_by?: APIOrderings
  interval?: APIIntervals
  search?: string
  is_featured?: boolean | 'all'
  is_private?: string
  type_ids?: PROMPT_TYPES[]
  enterprise_id?: number
  user_id?: string
  mode?: GetUserPromptMode
  is_premium?: boolean
  companiesSection?: boolean
  item_type?: ItemType
  elemental_type_id?: ElementalTypeEnum
  elemental_type_ids?: ElementalTypeEnum[]
  get_lists?: boolean
  is_template?: boolean
}

export interface GetElementalParentsQuery {
  elementIds: string[]
  limit?: number
}
interface Parent {
  id: string
  title: string
  url: string
}
interface GetElementalParentsResponse {
  id: string
  counts: number
  parents: Parent[]
}
interface CreateElementalResult {
  id: string
  userId: string
  title: string
  template: string
  url: string
  typeId: number
  priceTypeId: number
  isPrivate: true
  isLocked: false
  isPremium: false
  elementalTypeId: ElementalTypeEnum
  companyId: number
}

interface Command {
  elemental_id: string
  id: number
  template: string
  trigger: string
  created_at: string
  updated_at: string
  user_id: string
}

export interface ElemetalInfosData {
  isSaved: boolean
  isVoted: boolean
  counts?: {
    ratings: number
    comments: number
    votes: number
    saves: number
  }
}

export interface ElemetalInfosQuery {
  fields?: 'basic' | 'full'
}

// Elemental Requests
export const getElementals = async (
  query?: ElementalQuery,
  signal?: AbortSignal,
): Promise<null | Elemental[]> => {
  const endpoint = useParseQuery('/elementals', query)
  return filterResponse(
    await APIFetch(endpoint, {
      method: 'GET',
      body: query,
      signal,
    }),
  )
}

export const getElementalParents = async (query: GetElementalParentsQuery) => {
  let url = '/elementals-parents'
  url = useParseQuery(url, query)
  return filterResponse(
    await APIFetch<GetElementalParentsResponse[]>(url, {
      method: 'GET',
    }),
  )
}

export const getElementalsByTrigger = async (
  trigger: string,
): Promise<null | Command> => {
  if (!trigger)
    return null
  return filterResponse(
    await APIFetch(`/commands/${trigger}`, {
      method: 'GET',
    }),
  )
}

export const createElemental = async (
  body: Record<string, any>,
): Promise<null | CreateElementalResult> => {
  return filterResponse(
    await APIFetch('/elementals', {
      method: 'POST',
      body,
      timeout: 4000,
    }),
  )
}

export const duplicateElemental = async (
  id: string,
  listId?: string,
): Promise<null | Elemental> => {
  return filterResponse<Elemental & { elemental_type_id: string }>(
    await APIFetch(`/elementals/${id}/duplicate`, {
      method: 'POST',
      body: { listId },
    }),
  )
}

export const getElemental = async (id: string): Promise<null | Elemental> => {
  return filterResponse(
    await APIFetch(`/elementals/${id}`, {
      method: 'GET',
    }),
  )
}

export const getElementalInfos = async (
  id: string,
  query?: ElemetalInfosQuery,
): Promise<null | ElemetalInfosData> => {
  const url = `/elementals/${id}/infos`
  return filterResponse(
    await APIFetch(useParseQuery(url, query), {
      method: 'GET',
    }),
  )
}

export const updateElemental = async (
  id: string,
  body: Record<string, any>,
): Promise<null | {
  id: string
  url: string
}> => {
  return filterResponse(
    await APIFetch(`/elementals/${id}`, {
      method: 'PATCH',
      body,
    }),
  )
}

export const deleteElemental = async (id: string): Promise<boolean> => {
  return filterResponseBoolean(
    await APIFetch(`/elementals/${id}`, {
      method: 'DELETE',
    }),
  )
}

export const saveElemental = async (id: string): Promise<boolean> => {
  return filterResponseBoolean(
    await APIFetch(`/elementals/${id}/saves`, {
      method: 'POST',
    }),
  )
}

export const updateListItems = async (id: string, itemIds?: string[]) => {
  return filterResponse<boolean>(
    await APIFetch(`/elementals/${id}/items`, {
      method: 'PUT',
      body: { itemIds },
    }),
  )
}
export const addItemsInAList = async (id: string, itemIds?: string[]) => {
  return filterResponse<boolean>(
    await APIFetch(`/elementals/${id}/items`, {
      method: 'POST',
      body: { itemIds },
    }),
  )
}
export const removeItemsInAList = async (id: string, itemIds?: string[]) => {
  return filterResponse<boolean>(
    await APIFetch(`/elementals/${id}/items`, {
      method: 'DELETE',
      body: { itemIds },
    }),
  )
}

export const saveElementalInAList = async (
  listId: string,
  id: string,
  orderIndex?: number,
) => {
  return filterResponse<boolean>(
    await APIFetch(`/elementals/${listId}/items/${id}`, {
      method: 'POST',
      body: { orderIndex },
    }),
  )
}
export const unsaveElementalInAList = async (
  listId: string,
  id: string,
  orderIndex?: number,
) => {
  return filterResponse<boolean>(
    await APIFetch(`/elementals/${listId}/items/${id}`, {
      method: 'DELETE',
      body: { orderIndex },
    }),
  )
}

export const unsaveElemental = async (id: string): Promise<boolean> => {
  return filterResponseBoolean(
    await APIFetch(`/elementals/${id}/saves`, {
      method: 'DELETE',
    }),
  )
}

export const voteElemental = async (id: string): Promise<boolean> => {
  return filterResponseBoolean(
    await APIFetch(`/elementals/${id}/votes`, {
      method: 'POST',
    }),
  )
}

export const removeElementalVote = async (id: string): Promise<boolean> => {
  return filterResponseBoolean(
    await APIFetch(`/elementals/${id}/votes`, {
      method: 'DELETE',
    }),
  )
}

export const getFavorites = async (): Promise<null | FavoritedItem[]> => {
  return filterResponse(
    await APIFetch('/elementals?is_favorite=true&is_private=ALL', {
      method: 'GET',
    }),
  )
}

export const favoriteElemental = async (id: string) => {
  return filterResponse(
    await APIFetch(`/elementals/${id}/favorites`, {
      method: 'POST',
    }),
  )
}

export const removeElementalFavorite = async (id: string) => {
  return filterResponse(
    await APIFetch(`/elementals/${id}/favorites`, {
      method: 'DELETE',
    }),
  )
}

export const updateUseCountElemental = async (
  id: string,
  data: UseElementalData,
): Promise<boolean> => {
  return filterResponseBoolean(
    await APIFetch(`/elementals/${id}/uses`, {
      method: 'POST',
      body: data,
    }),
  )
}

export const buyElemental = async (
  id: string,
  value?: number,
  comment?: string,
): Promise<null | string> => {
  return filterResponse(
    await APIFetch<string>(`/elementals/${id}/buy?value=${value}`, {
      method: 'POST',
      baseUrl: useRuntimeConfig().public.marketplaceApiUrl,
      body: { comment },
    }),
  )
}

export const tipElemental = async (
  type: string,
  id: string,
  value: number,
  comment?: string,
): Promise<string | null> => {
  let url = `/tips/${id}/elementals`
  url = useParseQuery(url, { value })

  return filterResponse(
    await APIFetch(url, {
      method: 'POST',
      body: { value, comment },
      baseUrl: useRuntimeConfig().public.marketplaceApiUrl,
    }),
  )
}

export const uploadAttachment = async (
  id: string,
  value: File,
  isAdmin?: boolean,
): Promise<string | null> => {
  const url = `${isAdmin ? '/admin' : ''}/elementals/${id}/files`
  const formData = new FormData()
  formData.append('file', value)

  return filterResponse(
    await APIFetch(url, {
      method: 'POST',
      body: formData,
      isFormData: true,
    }),
  )
}

export const getFiles = async (
  productId: string,
): Promise<null | ElementalFile[]> => {
  return filterResponse(
    await APIFetch(`/elementals/${productId}/files`, {
      method: 'GET',
    }),
  )
}

export const getFileLink = async (
  productId: string,
  fileId: number,
): Promise<null | ElementalFile> => {
  return filterResponse(
    await APIFetch(`/elementals/${productId}/files/${fileId}`, {
      method: 'GET',
    }),
  )
}

export const deleteFile = async (
  productId: string,
  fileId: number,
  isAdmin?: boolean,
): Promise<boolean> => {
  return filterResponseBoolean(
    await APIFetch(
      `${isAdmin ? '/admin' : ''}/elementals/${productId}/files/${fileId}`,
      {
        method: 'DELETE',
      },
    ),
  )
}

type UpdateElementalAsAdmin = Partial<
  {
    isHidden: boolean
    isFeatured: boolean
    companiesSection: boolean
  } & ElementalFormData
>
export const updateElementalAsAdmin = async (
  idOrUrl: string,
  body: UpdateElementalAsAdmin,
): Promise<Elemental | null> => {
  return filterResponse(
    await APIFetch(`/admin/elementals/${idOrUrl}`, {
      body,
      method: 'PATCH',
    }),
  )
}

export const getOwnershipInformation = async (
  id: string,
  isAdmin?: boolean,
): Promise<null | OwnershipInformation> => {
  return filterResponse(
    await APIFetch<OwnershipInformation>(
      `${isAdmin ? '/admin' : ''}/elementals/${id}/information`,
      {
        method: 'GET',
        baseUrl: useRuntimeConfig().public.marketplaceApiUrl,
      },
    ),
  )
}

export const getLeads = async (
  id: string,
): Promise<null | OwnershipEmailLeads> => {
  return filterResponse(
    await APIFetch<OwnershipEmailLeads>(`/elementals/${id}/leads-info`, {
      method: 'GET',
    }),
  )
}

export const sendUseElementRegister = async (
  promptId: string,
  body: PromptUseCreate,
): Promise<boolean> => {
  return filterResponseBoolean(
    await APINativeFetch(`/elementals/${promptId}/uses`, {
      method: 'POST',
      body,
    }),
  )
}

export const uploadAvatar = async (
  id: string,
  avatar: File,
  isAdmin?: boolean,
) => {
  const formData = new FormData()
  formData.append('file', avatar)

  return filterResponseBoolean(
    await APIFetch(`${isAdmin ? '/admin' : ''}/elementals/${id}/avatar`, {
      method: 'PATCH',
      body: formData,
      isFormData: true,
    }),
  )
}
export const uploadImage = async (
  promptId: string,
  image: File,
  imageType: 'cover' | 'output',
  isAdmin?: boolean,
) => {
  const formData = new FormData()
  formData.append('file', image)

  return filterResponse<{ id: number; url: string }>(
    await APIFetch(
      `${isAdmin ? '/admin' : ''}/elementals/${promptId}/images?${
        imageType === 'output' ? 'is_output_image' : 'is_cover_image'
      }=true`,
      {
        method: 'POST',
        body: formData,
        isFormData: true,
      },
    ),
  )
}

export const deleteImage = async (
  elementalId: string,
  imageId: number,
  isAdmin?: boolean,
) => {
  return filterResponseBoolean(
    await APIFetch(
      `${isAdmin ? '/admin' : ''}/elementals/${elementalId}/images/${imageId}`,
      {
        method: 'DELETE',
      },
    ),
  )
}

type UpdateAsAdmin = {
  isHidden?: boolean
  isFeatured?: boolean
  companiesSection?: boolean
} & Partial<ElemetalInfosData>
export const updateAsAdmin = async (
  listIdOrUrl: string,
  body: UpdateAsAdmin,
): Promise<Elemental | null> => {
  return filterResponse(
    await APIFetch(`/admin/elementals/${listIdOrUrl}`, {
      body,
      method: 'PATCH',
    }),
  )
}

export async function generatePromptFromImages(elementalId: string) {
  return filterResponse<string>(
    await APIFetch(`/elementals/${elementalId}/generate-prompt`, {
      method: 'POST',
    }),
  )
}

export const setElementalTemplate = async (
  productId: string,
  isTemplate: boolean,
): Promise<null | TagData> => {
  return filterResponse(
    await APIFetch(`/elementals/${productId}/settings`, {
      method: 'PUT',
      body: { isTemplate },
    }),
  )
}

export const setElementalKnowledgeBase = async (
  productId: string,
  isKnowledgeBase: boolean,
): Promise<null | ElementalType[]> => {
  return filterResponse(
    await APIFetch(`/elementals/${productId}/settings`, {
      method: 'PUT',
      body: { isKnowledgeBase },
    }),
  )
}
